import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import Composer from './components/Composer'
import Conversation from './components/Conversation'
import QuotePanel from './components/QuotePanel'
import Sidebar from './components/Sidebar'

/**
 * The three views that are NOT the landing page, fetched when somebody actually goes to one.
 *
 * Lighthouse measured 40 KB of the entry bundle as unused on first load, and this is most of it: a
 * customer arriving to sell a device downloads, parses and compiles the sales list, the tracking view and
 * the explainer before they have chosen a category. Each is a separate chunk now, so the first screen
 * costs only what the first screen needs.
 */
const QuotesView = lazy(() => import('./components/QuotesView'))
const SaleView = lazy(() => import('./components/SaleView'))
const AboutView = lazy(() => import('./components/AboutView'))
import { trackPageView } from './state/analytics'
import { rupees } from './state/sale'
import { useAuth } from './state/auth'
import { useSaleEngine } from './state/useSaleEngine'

/**
 * The application shell: a collapsible left sidebar, the conversation, and a sale summary that OPENS over
 * the page from the right. The page itself never scrolls - html, body and .app are fixed to the viewport,
 * and each region owns its own scrolling - so the header and the composer stay where they are while a long
 * conversation moves underneath them.
 *
 * The summary is not a permanent column. The design docked it always, but a summary of what the customer
 * was just told does not earn 290px on the opening screen, where it has nothing in it. So it starts
 * closed and the conversation takes the room, and how it opens depends on how much room there is:
 *
 *   - 1051px and up it DOCKS, as a third column that grows from zero, and it opens itself once the device
 *     is known. Nothing is covered, so the customer can read the summary and answer the next question at
 *     the same time.
 *   - Below that it is a drawer over the page with a backdrop, and it opens only when asked, because
 *     opening it would hide the question being asked.
 *
 * Below 680px the sidebar becomes a left-hand drawer too. Every drawer has a backdrop, focus-visible
 * outlines and Escape to close.
 */
export default function App() {
  const { customer, expired, dismissExpiry } = useAuth()
  // The engine needs the customer for one thing only: skipping the pincode question for someone who
  // already has a pickup address on file.
  const engine = useSaleEngine(customer?.customerRegId)
  const navigate = useNavigate()
  const location = useLocation()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [summaryOpen, setSummaryOpen] = useState(false)

  /**
   * The two breakpoints, as STATE rather than as a function called during render.
   *
   * They were read with matchMedia at render time, which is only correct until the window is resized:
   * nothing re-rendered, so the shell went on believing it was on a phone after the window had been
   * widened. They must also agree exactly with styles.css, because the CSS decides whether the summary
   * is a docked column or a drawer and this file decides whether to put a backdrop behind it - and the
   * two disagreeing is a backdrop over a panel that is not floating.
   */
  const [isPhone, setIsPhone] = useState(() => window.matchMedia('(max-width:680px)').matches)
  const [isWide, setIsWide] = useState(() => window.matchMedia('(min-width:1051px)').matches)

  useEffect(() => {
    const phone = window.matchMedia('(max-width:680px)')
    const wide = window.matchMedia('(min-width:1051px)')
    const sync = () => {
      setIsPhone(phone.matches)
      setIsWide(wide.matches)
    }
    phone.addEventListener('change', sync)
    wide.addEventListener('change', sync)
    return () => {
      phone.removeEventListener('change', sync)
      wide.removeEventListener('change', sync)
    }
  }, [])

  const narrow = () => isPhone

  const closeDrawers = useCallback(() => {
    setSummaryOpen(false)
    setSidebarOpen(false)
  }, [])

  // Escape closes whichever drawer is open. Registered once, at the shell, so no individual drawer has to
  // remember to do it and none of them can disagree about what Escape means.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawers()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [closeDrawers])

  // Navigating on a phone must close the drawer that was used to navigate, or the new view is hidden
  // behind it.
  useEffect(() => closeDrawers(), [location.pathname, closeDrawers])

  // One analytics page view per route, because a single page application only ever loads once. The path
  // is rewritten first: see state/analytics.ts for what is stripped and why.
  useEffect(() => trackPageView(location.pathname), [location.pathname])

  const onSale = location.pathname === '/'
  const title = onSale
    ? 'New sale'
    : location.pathname === '/quotes'
      ? 'My sales'
      : location.pathname.startsWith('/sale/')
        ? 'Your sale'
        : 'How it works'

  /**
   * On a big screen the summary OPENS ITSELF once the device is known, because that is the moment it
   * starts having something to say: until a device is chosen it reads "let's find the exact model", and
   * opening it before then would be a panel of blanks.
   *
   * Once only, and never against the customer: `autoOpened` is what makes closing it stick, so it does
   * not reappear at the next answer. A new sale arms it again.
   *
   * Not on a phone or a tablet, where it is a drawer over the conversation - there, opening it on the
   * customer's behalf would hide the very question they are being asked.
   */
  const autoOpened = useRef(false)
  useEffect(() => {
    if (!isWide || autoOpened.current || !engine.sale.device) return
    autoOpened.current = true
    setSummaryOpen(true)
  }, [isWide, engine.sale.device])

  const startNewSale = () => {
    autoOpened.current = false
    setSummaryOpen(false)
    engine.reset()
    navigate('/')
  }

  // Only when the summary is FLOATING. On a big screen it is a docked column that pushes the conversation
  // aside, and a backdrop over that would grey out - and swallow the clicks meant for - the answer chips
  // the customer is looking at.
  const backdropVisible = (!isWide && summaryOpen) || (isPhone && sidebarOpen)

  const appClass = [
    'app',
    sidebarCollapsed && !narrow() ? 'sidebar-collapsed' : '',
    sidebarOpen ? 'sidebar-open' : '',
    summaryOpen ? 'summary-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const sidebarVisible = narrow() ? sidebarOpen : !sidebarCollapsed

  return (
    <div className={appClass} id="app">
      <Sidebar
        onNewSale={startNewSale}
        onSignIn={() => {
          // The form lives in the conversation, so being anywhere else - My sales, How it works - has to
          // become being there, or tapping Sign in would look like it did nothing.
          engine.requestSignIn()
          navigate('/')
        }}
      />

      <main className="main">
        <header className="top">
          <button
            className="icon-button"
            type="button"
            aria-label={sidebarVisible ? 'Hide sidebar' : 'Show sidebar'}
            aria-controls="sidebar"
            aria-expanded={sidebarVisible}
            onClick={() => {
              if (narrow()) {
                setSidebarOpen((open) => !open)
                setSummaryOpen(false)
              } else {
                setSidebarCollapsed((collapsed) => !collapsed)
              }
            }}
          >
            ☰
          </button>
          <span className="mobile-brand">
            <i>↗</i> sellgpt.
          </span>
          <h1>{title}</h1>
          <span className="top-right">
            {engine.assistantAvailable ? '● Selling assistant' : '● Guided selling'}
          </span>
          <button
            className="quote-toggle"
            type="button"
            aria-controls="saleSummary"
            aria-expanded={summaryOpen}
            onClick={() => {
              setSummaryOpen((open) => !open)
              setSidebarOpen(false)
            }}
          >
            {/* With the panel closed by default, the button is the only thing telling the customer the
                summary exists - so once there is a price it carries it, and opening it is a choice rather
                than a guess about what is behind the label. */}
            Your sale{engine.sale.quote ? ` · ${rupees(engine.sale.quote.sellerPayout)}` : ''}
          </button>
        </header>

        {/* The banner the demo used for "this is not real" now carries the two things that ARE worth
            saying: that the assistant is unavailable, or that a session ended. Neither is an error the
            customer caused, and neither loses anything they have answered. */}
        {!engine.assistantAvailable && (
          <div className="banner" role="status">
            The selling assistant is unavailable, so I'll take you through it step by step. Prices and
            device details still come from Cashit.
          </div>
        )}
        {expired && (
          <div className="banner" role="status">
            Your session ended, so you have been signed out. Your sale is saved — verify your number again
            to finish it.{' '}
            <button type="button" className="footer-link" onClick={dismissExpiry} style={{ background: 'none', border: 0 }}>
              Dismiss
            </button>
          </div>
        )}

        <div className="chat" id="chat">
          <Suspense fallback={<div className="chat-inner" aria-busy="true" />}>
          <Routes>
            <Route path="/" element={<Conversation engine={engine} />} />
            <Route path="/quotes" element={<QuotesView customer={customer} />} />
            {/* One placed sale, tracked through to completion. A real, shareable URL - IIS serves
                index.html for it and the router takes it from there. */}
            <Route path="/sale/:leadNumber" element={<SaleView />} />
            <Route path="/how-it-works" element={<AboutView />} />
            {/* An unknown deep link is not a dead end: IIS already served the application, so the
                application shows the sale rather than a 404 nobody can act on. */}
            <Route path="*" element={<Conversation engine={engine} />} />
          </Routes>
          </Suspense>
        </div>

        {onSale && <Composer engine={engine} />}
      </main>

      <QuotePanel engine={engine} open={summaryOpen} onClose={() => setSummaryOpen(false)} />

      <div className="backdrop" hidden={!backdropVisible} onClick={closeDrawers} />
    </div>
  )
}
